import { queryOptions, useQuery } from '@tanstack/react-query';

import { Cloud } from '@cast/shared-types';
import {
  CloudAssetIntegrationsCommitmentStatus,
  CloudAssetIntegrationsScope,
} from '@cast/types-inventory';

import { apiClient } from '@cast/infrastructure-api';
import { useCurrentOrganizationId } from '@cast/infrastructure-organization';
import { useOrganizations } from '@cast/shared-organization-context-data-access';

const INVENTORY_ASSET_DISCOVERY_COMMAND_QUERY_KEY =
  'inventory-asset-discovery-command';

const providerToParam = (provider: Cloud) => {
  switch (provider) {
    case Cloud.gcp:
      return 'GCP';
    case Cloud.aws:
      return 'AWS';
    case Cloud.azure:
      return 'AZURE';
    case Cloud.oracle:
      return 'ORACLE';
    default:
      return 'CLOUD_UNSPECIFIED';
  }
};

type QueryArgs = {
  provider?: Cloud;
  scope?: CloudAssetIntegrationsScope;
  commitmentAutoAssignment?: boolean;
  enabled?: boolean;
};

const getAssetDiscoveryCommandQueryKey = ({
  organizationId,
  provider,
  scope,
  commitmentAutoAssignment,
}: Pick<QueryArgs, 'provider' | 'scope' | 'commitmentAutoAssignment'> & {
  organizationId?: string;
} = {}) =>
  [
    INVENTORY_ASSET_DISCOVERY_COMMAND_QUERY_KEY,
    organizationId,
    provider,
    scope,
    commitmentAutoAssignment ?? false,
  ] as const;

const getAssetDiscoveryCommandQueryOptions = ({
  organizationId,
  provider,
  scope,
  commitmentAutoAssignment,
  enabled,
}: { organizationId?: string } & QueryArgs) =>
  queryOptions({
    queryKey: getAssetDiscoveryCommandQueryKey({
      organizationId,
      provider,
      scope,
      commitmentAutoAssignment,
    }),
    queryFn: async () => {
      const { data } = await apiClient.inventory.getAssetDiscoveryCommand({
        organizationId: organizationId!,
        ...(provider && { provider: providerToParam(provider) }),
        scope,
        ...(commitmentAutoAssignment && {
          integrationSettingsCommitmentsDefaultStatus:
            CloudAssetIntegrationsCommitmentStatus.ACTIVE,
          integrationSettingsCommitmentsAssignAutomatically: true,
        }),
      });
      return data;
    },
    enabled: enabled && !!organizationId,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });

export const useAssetDiscoveryCommandQuery = ({
  provider,
  scope,
  commitmentAutoAssignment,
  enabled = true,
}: QueryArgs = {}) => {
  const { currentOrganization } = useOrganizations();
  const currentOrganizationId = useCurrentOrganizationId();
  const organizationId = currentOrganization?.id ?? currentOrganizationId;

  const { isLoading, isFetching, data, error, refetch } = useQuery(
    getAssetDiscoveryCommandQueryOptions({
      organizationId,
      provider,
      scope,
      commitmentAutoAssignment,
      enabled,
    })
  );

  return {
    isLoading: isLoading || isFetching,
    script: data?.command,
    trackingSessionId: data?.trackingSessionId,
    error,
    refetch,
  };
};
